import makeStyles from '@material-ui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@material-ui/core';
import Button from 'components/CustomButtons/Button';
import { FindInPage, CalendarToday, Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ReactTable from 'react-table';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import classNames from 'classnames';

import ReportPreviewTitleRow from './ReportPreviewTitleRow';

const useStyles = makeStyles({
  button: { borderRadius: '30px' },
  header: {
    color: grayColor[16],
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  reactTable: {
    marginTop: '20px',
  },
  icon: { marginRight: '5px' },
  paper: {
    width: '90%',
    maxWidth: '90%',
    height: '100%',
    margin: '0',
  },
  dialogHeaderWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  dialogHeader: {
    padding: '24px',
    fontWeight: '500',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '64px',
  },
  pdfReader: {
    marginTop: '20px',
  },
  tableValue: {
    wordBreak: 'break-word',
  },
  filterInput: {
    width: '100%',
  },
});

const ReportPreview = ({
  date,
  dateFrom,
  dateTo,
  dateType,
  excelContent = {},
  filePreview = '',
  groupClient,
  includeShopElements,
  includeSubscriptions,
  isOpen = false,
  multiplier,
  packedDate,
  parameters = [],
  recipeTags,
  recipeTagsMode,
  selectedBrands,
  selectedDeliveryPoint,
  selectedDrivers,
  selectedMealCategories,
  selectedSubBrands,
  selectedVariant,
  selectedZoneCategories,
  shouldBeConsumedTo,
  showMultiplier,
  title,
  useSubBrands,
  useVariants,
  useZoneCategories,
  downloadPdf,
  downloadXls,
  onClose,
}) => {
  const { username, firstName, lastName } = useSelector(
    ({ Auth }) => Auth.user
  );
  const { t } = useTranslation();
  const classes = useStyles();

  const reportName = title.split('-').pop().trim();

  const hasExcel = excelContent.sheets?.length > 0;
  const sheet = hasExcel ? excelContent.sheets[0] : {};

  let columnConfig = [];
  if (hasExcel) {
    columnConfig = sheet.headers.map(({ id, name, type }) => ({
      id,
      Header: () => (
        <div style={{ marginBottom: '10px' }}>
          {name}
          <span className="DataGrid-arrow" />
        </div>
      ),
      accessor: row => {
        const data =
          type === 'integer' && isNaN(parseInt(row[id], 10))
            ? ''
            : parseInt(row[id], 10);

        return type === 'integer' ? (
          data
        ) : (
          <span
            className={classes.tableValue}
            dangerouslySetInnerHTML={{ __html: row[id] }}
          />
        );
      },
      sortMethod: (a, b) => {
        const getText = val =>
          typeof val === 'object' && val?.props?.children
            ? String(val.props.children)
            : String(val);

        const valA = getText(a);
        const valB = getText(b);

        const numA = Number(valA.replace(',', '.'));
        const numB = Number(valB.replace(',', '.'));

        const isNumeric = !isNaN(numA) && !isNaN(numB);

        if (isNumeric) {
          return numA - numB;
        }

        return valA.toLowerCase().localeCompare(valB.toLowerCase(), 'pl');
      },
      filterable: true,
      filterMethod: (filter, row) => {
        const filterId = filter.pivotId || filter.id;
        const rawValue = row[filterId];
        const cellValue =
          typeof rawValue === 'object' && rawValue.props
            ? rawValue.props.children
            : rawValue;

        return String(cellValue)
          .toLowerCase()
          .includes(String(filter.value || '').toLowerCase());
      },
    }));
  }

  const data = hasExcel ? sheet.data : [];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        classes: {
          root: classes.paper,
        },
      }}
    >
      <div className={classes.dialogHeaderWrapper}>
        <div className={classes.dialogHeader}>
          <span className={classes.header}>
            {t('reportPreview.previewTitle')}
          </span>
          <div className={classes.iconWrapper}>
            <FindInPage className={classes.icon} />
            <span className={classes.header}>{reportName}</span>
          </div>
          <div className={classes.iconWrapper}>
            <CalendarToday className={classes.icon} />
            <span className={classes.header}>
              {moment(new Date()).format('ddd, DD MMM YYYY HH:mm')}
            </span>
          </div>
          <Button
            color="success"
            className={classes.button}
            onClick={hasExcel ? downloadXls : downloadPdf}
          >
            {hasExcel ? t('reports.gReport') : t('reportPreview.generatePdf')}
          </Button>
        </div>
        <Button color="transparent" onClick={onClose}>
          <Close className={classes.icon} fontSize="large" />
        </Button>
      </div>
      <DialogContent className={classes.dialogContent}>
        {parameters.length > 0 ? (
          parameters.map((parameter, index) => {
            // eslint-disable-next-line no-underscore-dangle
            const {
              _key,
              brands,
              dateFrom: paramDateFrom,
              dateTo: paramDateTo,
              includeSubscriptions: paramIncludeSubscriptions,
              multiplier: paramMultiplier,
              subBrands,
              zoneCategories,
              isIndividualMultipliersEnabled,
            } = parameter;

            return (
              <ReportPreviewTitleRow
                key={_key}
                dateFrom={paramDateFrom}
                dateTo={paramDateTo}
                firstName={firstName}
                includeSubscriptions={paramIncludeSubscriptions}
                individualMultipliersEnabled={isIndividualMultipliersEnabled}
                isFirstRow={index === 0}
                lastName={lastName}
                multiplier={paramMultiplier}
                recipeTags={recipeTags}
                recipeTagsMode={recipeTagsMode}
                selectedBrands={brands}
                selectedSubBrands={subBrands}
                selectedZoneCategories={zoneCategories}
                showMultiplier={showMultiplier}
                username={username}
                useSubBrands={useSubBrands}
                useZoneCategories={useZoneCategories}
              />
            );
          })
        ) : (
          <ReportPreviewTitleRow
            date={date}
            dateFrom={dateFrom}
            dateTo={dateTo}
            dateType={dateType}
            firstName={firstName}
            groupClient={groupClient}
            includeShopElements={includeShopElements}
            includeSubscriptions={includeSubscriptions}
            isFirstRow={true}
            lastName={lastName}
            multiplier={multiplier}
            packedDate={packedDate}
            recipeTags={recipeTags}
            recipeTagsMode={recipeTagsMode}
            selectedBrands={selectedBrands}
            selectedDeliveryPoint={selectedDeliveryPoint}
            selectedDrivers={selectedDrivers}
            selectedMealCategories={selectedMealCategories}
            selectedSubBrands={selectedSubBrands}
            selectedVariant={selectedVariant}
            selectedZoneCategories={selectedZoneCategories}
            shouldBeConsumedTo={shouldBeConsumedTo}
            showMultiplier={showMultiplier}
            username={username}
            useSubBrands={useSubBrands}
            useVariants={useVariants}
            useZoneCategories={useZoneCategories}
          />
        )}
        {filePreview && (
          <object
            className={classes.pdfReader}
            data={filePreview}
            type="application/pdf"
            width="100%"
            height="100%"
          >
            {t('reportPreview.pdfNotDisplayed')}
          </object>
        )}
        {hasExcel && (
          <ReactTable
            className={classNames('-striped -highlight', classes.reactTable)}
            data={data}
            sortable={true}
            pageSizeOptions={[10, 20, 50, 100]}
            columns={columnConfig}
            filterable={true}
            defaultPageSize={20}
            showPaginationTop={true}
            showPaginationBottom={false}
            previousText={t('dataGrid.prevPage')}
            nextText={t('dataGrid.nextPage')}
            noDataText={t('dataGrid.notFound')}
            pageText={t('dataGrid.page')}
            ofText={t('dataGrid.pageOf')}
            rowsText={t('dataGrid.results')}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportPreview;
